import { useState } from "react";
import { useFormikContext } from "formik";
import { useTranslations } from "use-intl";
import { Field, Label } from "@/components/catalyst/fieldset";
import { SwitchControl } from "@/lib/Components/Form/Inputs/SwitchControl";
import { DistanceAllowedInput } from "@/lib/Components/Form/Inputs/DistanceAllowedInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { useHireUnitLabels } from "@/app/Relocations/Forms/relocationFormHooks";

export function RelocationDistanceFields() {
  const t = useTranslations("relocation");
  const tForm = useTranslations("relocation.form");
  const tSteps = useTranslations("relocation.form.steps");
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<RelocationFormValues>();
  const { unitLabel } = useHireUnitLabels();
  const [hasChosenCapped, setHasChosenCapped] = useState(false);
  const [focusToken, setFocusToken] = useState(0);

  const distanceLabel = t(`distance_allowed_label.${values.measurement}`);
  const isUnlimited = values.distance_allowed == null && !hasChosenCapped;

  async function handleUnlimitedChange(unlimited: boolean) {
    setHasChosenCapped(!unlimited);

    if (unlimited) {
      await setFieldTouched("distance_allowed", false, false);
      await setFieldValue("distance_allowed", null);
      return;
    }

    setFocusToken((token) => token + 1);
  }

  return (
    <>
      <Field className="col-span-full">
        <Label>{tSteps("unlimitedDistance", { label: distanceLabel })}</Label>
        <SwitchControl checked={isUnlimited} onChange={handleUnlimitedChange} />
      </Field>

      {isUnlimited ? null : (
        <>
          <DistanceAllowedInput
            key={focusToken}
            autoFocus={focusToken > 0}
            name="distance_allowed"
            tooltip={tForm("distanceTooltip")}
            label={distanceLabel}
            placeholder={tSteps("distancePlaceholder")}
            measurement={values.measurement}
            className="col-span-3"
            validate={(value) =>
              value == null || value <= 0
                ? tSteps("distanceRequired")
                : undefined
            }
          />

          {values.extra_hire_units_allowed ? (
            <DistanceAllowedInput
              label={`${t(`extra_distance_label.${values.measurement}`)} ${tForm("perExtraUnit", { unit: unitLabel })}`}
              name="extra_distance_allowed_per_extra_hire_unit"
              measurement={values.measurement}
              className="col-span-3"
              suffix={`/extra ${unitLabel}`}
              tooltip={tForm("extraDistanceTooltip")}
            />
          ) : null}

          <CurrencyInput
            currency={values.currency}
            name="excess_distance_rate"
            label={tForm("excessRateLabel", {
              label: distanceLabel.toLowerCase(),
            })}
            suffix={values.measurement === "IMPERIAL" ? "/ml" : "/km"}
            className="col-span-3"
            tooltip={tForm("excessRateTooltip")}
          />
        </>
      )}
    </>
  );
}
